import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import type {
  Account,
  AccountAssignments,
  SheetImportPreview,
  SheetVerifiedRow,
  SheetRowStatus,
} from '../api';
import { Button, ErrorBanner, Spinner, TextArea } from '../ui/primitives';
import { Tag, type TagTone } from '../ui/components/Tag';
import { usePinGate } from '../auth/pinGate';

interface AssignmentRates {
  uplineRate: string;
  clientRate: string | null;
}

interface ManualRowSummary {
  accountId: number;
  pointsStated: string;
  clientPointsStated: string | null;
  rateStated: string;
}

interface GridRow {
  accountId: number;
  canonicalName: string;
  assignment: AssignmentRates | null;
  equalSpread: boolean;
  originalEqualSpread: boolean;
  uplinePoints: string;
  clientPoints: string;
}

interface Props {
  batchId: number;
  uplinePartyId: number;
  /** The batch's week (Monday) — used to pre-fill points from a pasted sheet. */
  weekMonday: string;
  onSaved: (count: number) => void;
  onClose: () => void;
}

/** Rates are stored as decimal multipliers; show them as plain percent. */
function fmtPct(rate: string | null | undefined): string {
  if (rate === null || rate === undefined || rate === '') return '—';
  const n = Number(rate);
  return Number.isFinite(n) ? `${+(n * 100).toFixed(4)}%` : String(rate);
}

function calcAmt(pts: string, rate: string): string {
  const p = parseFloat(pts.replace(/,/g, ''));
  const r = parseFloat(rate);
  if (!Number.isFinite(p) || !Number.isFinite(r) || rate === '') return '';
  const val = p * r;
  return val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Raw numeric amount (no formatting) — for summing display-only totals. */
function amtNum(pts: string, rate: string | null | undefined): number {
  const p = parseFloat((pts ?? '').replace(/,/g, ''));
  const r = parseFloat(rate ?? '');
  if (!Number.isFinite(p) || !Number.isFinite(r)) return 0;
  return p * r;
}

/** Format a number as INR money (display only; never persisted). */
function fmtMoney(n: number): string {
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Sheet-verification status → chip tone + short label (reconciliation cockpit). */
function statusTone(s: SheetRowStatus): TagTone {
  switch (s) {
    case 'READY':
      return 'pos';
    case 'RATE_MISMATCH':
    case 'CLIENT_MISMATCH':
      return 'warn';
    case 'ARITH_MISMATCH':
    case 'PARSE_ERROR':
      return 'neg';
    default:
      return 'neutral';
  }
}
function statusLabel(s: SheetRowStatus): string {
  switch (s) {
    case 'READY':
      return '✓ matches sheet';
    case 'RATE_MISMATCH':
      return 'rate ≠ sheet';
    case 'ARITH_MISMATCH':
      return 'arithmetic off';
    case 'CLIENT_MISMATCH':
      return 'POC ≠ sheet';
    case 'NO_ASSIGNMENT':
      return 'no assignment';
    case 'UNRESOLVED_NAME':
      return 'unmatched name';
    case 'PARSE_ERROR':
      return 'unreadable';
    default:
      return s;
  }
}

/** Detects viewport < 768 px (below the Tailwind `md:` breakpoint). */
function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e: MediaQueryListEvent) => setMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return mobile;
}

export function ManualEntryGrid({ batchId, uplinePartyId, weekMonday, onSaved, onClose }: Props) {
  const qc = useQueryClient();
  const { withPin } = usePinGate();
  const [error, setError] = useState<string | null>(null);
  const isMobile = useIsMobile();

  // Paste-from-sheet: parse + verify server-side, then pre-fill the grid's
  // point inputs. The client comes from each account's Assignment (not the
  // sheet POC), so the fill matches by ACCOUNT; the user reviews and saves.
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteBusy, setPasteBusy] = useState(false);
  const [pasteErr, setPasteErr] = useState<string | null>(null);
  // ── Reconciliation state (set when the upline sheet is pasted) ──────────────
  // Keep the full per-row verification (status + diffs + sheet cost) the engine
  // returns, instead of throwing it away — this is what surfaces the six
  // Batch-#6 mistake types (wrong rate, 10× typo, dual-points divergence, …).
  const [verifyByAccount, setVerifyByAccount] = useState<Record<number, SheetVerifiedRow>>({});
  const [sheetExceptions, setSheetExceptions] = useState<SheetVerifiedRow[]>([]);
  const [sheetOrder, setSheetOrder] = useState<Record<number, number>>({});
  const [sheetOrderMode, setSheetOrderMode] = useState(false);
  const [sheetMeta, setSheetMeta] = useState<
    { filled: number; total: number; missingColumns: string[] } | null
  >(null);

  const accountsQ = useQuery<Account[]>({
    queryKey: ['accounts', 'upline', uplinePartyId],
    queryFn: () => api.get<Account[]>(`/api/accounts?upline=${uplinePartyId}&status=active`),
  });

  const ratesQ = useQuery<Map<number, AssignmentRates>>({
    queryKey: ['accounts', 'upline', uplinePartyId, 'rates'],
    enabled: (accountsQ.data?.length ?? 0) > 0,
    queryFn: async () => {
      const results = await Promise.allSettled(
        accountsQ.data!.map((a) =>
          api.get<AccountAssignments>(`/api/accounts/${a.id}/assignments`).then((r) => ({
            accountId: a.id,
            uplineRate: r.activeAssignment?.uplineRate ?? null,
            clientRate: r.activeAssignment?.clientRate ?? null,
          })),
        ),
      );
      const map = new Map<number, AssignmentRates>();
      for (const r of results) {
        if (r.status === 'fulfilled' && r.value.uplineRate) {
          map.set(r.value.accountId, {
            uplineRate: r.value.uplineRate,
            clientRate: r.value.clientRate,
          });
        }
      }
      return map;
    },
  });

  const existingRowsQ = useQuery<{ rows: ManualRowSummary[] }>({
    queryKey: ['batch', batchId, 'manual-rows'],
    queryFn: () => api.get<{ rows: ManualRowSummary[] }>(`/api/batches/${batchId}/manual-rows`),
  });

  const [uplinePoints, setUplinePoints] = useState<Record<number, string>>({});
  const [clientPoints, setClientPoints] = useState<Record<number, string>>({});
  const [spreads, setSpreads] = useState<Record<number, boolean>>({});
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [prePopulated, setPrePopulated] = useState(false);

  const uplineRefs = useRef<(HTMLInputElement | null)[]>([]);
  const clientRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (prePopulated) return;
    if (!accountsQ.data || !existingRowsQ.data) return;

    const existingByAccountId = new Map(
      existingRowsQ.data.rows.map((r) => [r.accountId, r]),
    );

    const newUpline: Record<number, string> = {};
    const newClient: Record<number, string> = {};
    const newSelected: Record<number, boolean> = {};

    for (const acct of accountsQ.data) {
      const saved = existingByAccountId.get(acct.id);
      if (saved) {
        newUpline[acct.id] = saved.pointsStated;
        newClient[acct.id] = saved.clientPointsStated ?? '';
        newSelected[acct.id] = true;
      } else {
        newSelected[acct.id] = false;
      }
    }

    setUplinePoints(newUpline);
    setClientPoints(newClient);
    setSelected(newSelected);
    setPrePopulated(true);
  }, [accountsQ.data, existingRowsQ.data, prePopulated]);

  const rows: GridRow[] = (accountsQ.data ?? []).map((a) => {
    const assignment = ratesQ.data?.get(a.id) ?? null;
    const originalEqualSpread = a.equalSpread;
    return {
      accountId: a.id,
      canonicalName: a.canonicalName,
      assignment,
      equalSpread: spreads[a.id] !== undefined ? spreads[a.id]! : originalEqualSpread,
      originalEqualSpread,
      uplinePoints: uplinePoints[a.id] ?? '',
      clientPoints: clientPoints[a.id] ?? '',
    };
  });

  const rowsToSave = rows.filter(
    (r) => r.assignment !== null && selected[r.accountId] === true,
  );
  // "Same" is a PER-ENTRY choice only — it decides whether this save sends one
  // points figure (client mirrors upline) or separate client points. It does
  // NOT touch the account's stored preference.
  const canSave = rowsToSave.length > 0;

  const selectedCount = rows.filter((r) => selected[r.accountId] === true).length;
  const allSelected = rows.length > 0 && rows.every((r) => selected[r.accountId] === true);
  const someSelected = rows.some((r) => selected[r.accountId] === true);

  // Display order: optionally follow the pasted sheet's order so the on-screen
  // list matches the WhatsApp screenshot top-to-bottom (the user reads down it).
  const displayRows = sheetOrderMode
    ? [...rows].sort(
        (a, b) =>
          (sheetOrder[a.accountId] ?? Number.POSITIVE_INFINITY) -
          (sheetOrder[b.accountId] ?? Number.POSITIVE_INFINITY),
      )
    : rows;

  // Running totals over the rows that WILL be saved — the money cross-check
  // that would have caught Batch #6's ₹32K margin gap. Display-only (decimal
  // guard does not scan web/), so plain Number math is fine here.
  const totals = rowsToSave.reduce(
    (acc, r) => {
      const uRate = r.assignment?.uplineRate ?? '';
      const cRate = r.assignment?.clientRate ?? r.assignment?.uplineRate ?? '';
      const cPts = r.equalSpread ? r.uplinePoints : r.clientPoints;
      acc.uplineCost += amtNum(r.uplinePoints, uRate);
      acc.clientAmount += amtNum(cPts, cRate);
      return acc;
    },
    { uplineCost: 0, clientAmount: 0 },
  );
  const totalMargin = totals.clientAmount - totals.uplineCost;

  // EXTRA = selected accounts the pasted sheet did NOT contain (Batch #6's
  // "extra row" — e.g. Allpanel). Only meaningful after a paste.
  const extraRows = sheetMeta
    ? rows.filter((r) => selected[r.accountId] === true && verifyByAccount[r.accountId] === undefined)
    : [];
  // Sheet rows whose name matched no account at all — the dangerous "missed
  // row" (Batch #6's dropped NAMO1). Other-upline noise is intentionally ignored.
  const unresolvedSheet = sheetExceptions.filter((e) => e.status === 'UNRESOLVED_NAME');

  const saveMut = useMutation({
    mutationFn: async () => {
      await api.post(`/api/batches/${batchId}/manual-rows`, {
        rows: rowsToSave.map((r) => ({
          accountId: r.accountId,
          pointsStated: r.uplinePoints.trim() || '0',
          rateStated: r.assignment!.uplineRate,
          clientPointsStated: !r.equalSpread
            ? (r.clientPoints.trim() || '0')
            : null,
        })),
      });
      return rowsToSave.length;
    },
    onSuccess: (count) => {
      void qc.invalidateQueries({ queryKey: ['batch', batchId] });
      void qc.invalidateQueries({ queryKey: ['accounts', 'upline', uplinePartyId] });
      onSaved(count);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  const focusableIdxs = displayRows.reduce<number[]>((acc, r, i) => {
    if (r.assignment !== null) acc.push(i);
    return acc;
  }, []);

  function toggleAll() {
    const next = !allSelected;
    const update: Record<number, boolean> = {};
    for (const r of rows) update[r.accountId] = next;
    setSelected((p) => ({ ...p, ...update }));
  }

  async function fillFromSheet(): Promise<void> {
    setPasteBusy(true);
    setPasteErr(null);
    try {
      const preview = await api.post<SheetImportPreview>('/api/batches/sheet-import/preview', {
        weekMonday,
        tsv: pasteText,
      });
      // The preview is GLOBAL (every upline). Split rows into: belongs-to-this-grid
      // (keep the full verification) vs everything else (exceptions panel).
      const gridAccountIds = new Set((accountsQ.data ?? []).map((a) => a.id));
      const nextUpline: Record<number, string> = {};
      const nextClient: Record<number, string> = {};
      const nextSpread: Record<number, boolean> = {};
      const nextSelected: Record<number, boolean> = {};
      const nextVerify: Record<number, SheetVerifiedRow> = {};
      const nextOrder: Record<number, number> = {};
      const nextExceptions: SheetVerifiedRow[] = [];
      let filled = 0;
      for (const row of preview.rows) {
        const mine = row.accountId !== undefined && gridAccountIds.has(row.accountId);
        if (!mine) {
          // Unmatched name, or a row for a DIFFERENT upline → exception list.
          nextExceptions.push(row);
          continue;
        }
        const id = row.accountId!;
        // KEEP the verification verbatim — this is the reconciliation baseline.
        nextVerify[id] = row;
        nextOrder[id] = row.rowIndex;
        // Soft-exception rows (RATE/CLIENT/ARITH mismatch) still stage points, so
        // they fill + show their chip rather than being silently dropped.
        if (row.uplinePoints !== undefined) {
          const u = row.uplinePoints;
          const c = row.clientPoints ?? u;
          nextUpline[id] = u;
          nextSelected[id] = true;
          if (c !== u) {
            nextClient[id] = c;
            nextSpread[id] = false; // distinct dual points (divergence)
          } else {
            nextSpread[id] = true; // client mirrors upline
          }
          filled += 1;
        }
      }
      setUplinePoints((p) => ({ ...p, ...nextUpline }));
      setClientPoints((p) => ({ ...p, ...nextClient }));
      setSpreads((p) => ({ ...p, ...nextSpread }));
      setSelected((p) => ({ ...p, ...nextSelected }));
      // REPLACE (not merge) the sheet-derived reconciliation state, so a
      // re-paste fully supersedes the prior paste. Accounts that were in the
      // old paste but not the new one lose their verification → they surface as
      // EXTRA (selected but not on the sheet) instead of keeping a stale chip.
      setVerifyByAccount(nextVerify);
      setSheetOrder(nextOrder);
      setSheetExceptions(nextExceptions);
      setSheetMeta({ filled, total: preview.rows.length, missingColumns: preview.missingColumns });
      if (filled > 0) setPasteOpen(false);
    } catch (e) {
      setPasteErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setPasteBusy(false);
    }
  }

  const isLoading = accountsQ.isLoading || existingRowsQ.isLoading;

  return (
    <div className="flex flex-col gap-4">
      {/* Paste-from-sheet helper — fills the point inputs below. */}
      <div className="rounded-lg border border-border bg-surface-raised/40 p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="text-small font-medium text-ink">
            Paste master sheet
            <span className="ml-2 font-normal text-caption text-ink-muted">
              fills Points below — you review &amp; save
            </span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setPasteOpen((o) => !o)}>
            {pasteOpen ? 'Hide ↑' : 'Paste ↓'}
          </Button>
        </div>
        {sheetMeta && (
          <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-caption text-emerald-800">
            Filled {sheetMeta.filled} of {sheetMeta.total} sheet row(s). Each filled row below
            shows how it reconciles against the sheet.
            {sheetMeta.missingColumns.length > 0 && (
              <span className="text-warn">
                {' '}Sheet is missing column(s): {sheetMeta.missingColumns.join(', ')}.
              </span>
            )}
          </div>
        )}
        {pasteOpen && (
          <div className="mt-2 space-y-2">
            <TextArea
              label=""
              rows={5}
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              placeholder={'Paste the sheet rows (with the header row). Points fill in below; rates come from each account’s assignment.'}
              className="font-mono text-caption"
            />
            {pasteErr && <ErrorBanner>{pasteErr}</ErrorBanner>}
            <Button
              size="sm"
              disabled={pasteBusy || pasteText.trim().length === 0}
              onClick={() => void fillFromSheet()}
            >
              {pasteBusy ? <Spinner /> : 'Fill points from sheet'}
            </Button>
          </div>
        )}
      </div>

      {/* ── Reconciliation cockpit (after a paste) ─────────────────────────── */}
      {sheetMeta && (
        <ReconciliationBar
          totals={totals}
          totalMargin={totalMargin}
          rowCount={rowsToSave.length}
          sheetOrderMode={sheetOrderMode}
          setSheetOrderMode={setSheetOrderMode}
        />
      )}
      {sheetMeta && (unresolvedSheet.length > 0 || extraRows.length > 0) && (
        <ExceptionsPanel
          unresolved={unresolvedSheet}
          extraNames={extraRows.map((r) => r.canonicalName)}
        />
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 py-4 text-caption text-ink-muted">
          <Spinner /> Loading accounts…
        </div>
      ) : rows.length === 0 ? (
        <p className="text-caption text-ink-muted">No active accounts found for this upline.</p>
      ) : isMobile ? (
        <ManualEntryListMobile
          rows={displayRows}
          verifyByAccount={verifyByAccount}
          hasSheet={sheetMeta !== null}
          ratesLoading={ratesQ.isLoading}
          uplinePoints={uplinePoints}
          setUplinePoints={setUplinePoints}
          clientPoints={clientPoints}
          setClientPoints={setClientPoints}
          spreads={spreads}
          setSpreads={setSpreads}
          selected={selected}
          setSelected={setSelected}
          saveCount={rowsToSave.length}
          selectedCount={selectedCount}
          totalRows={rows.length}
          canSave={canSave}
          onSave={() => void withPin(() => saveMut.mutateAsync())}
          isSaving={saveMut.isPending}
          onCancel={onClose}
          error={error}
        />
      ) : (
        <>
          <p className="text-caption text-ink-muted">
            Check the rows you want to update and enter Points P&amp;L. Unchecked rows are left
            untouched. Toggle <span className="font-medium text-ink">Same</span> to share
            one points figure across upline and client, or uncheck to enter separately.
          </p>

          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-caption">
              <colgroup>
                <col className="w-[36px]" />
                <col />
                {sheetMeta && <col className="w-[190px]" />}
                <col className="w-[120px]" />
                <col className="w-[60px]" />
                <col className="w-[220px]" />
                <col className="w-[160px]" />
              </colgroup>
              <thead className="bg-surface-raised/60">
                <tr>
                  <th className="px-2 py-2 text-center">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-border bg-surface accent-brand"
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someSelected && !allSelected;
                      }}
                      onChange={toggleAll}
                      title={allSelected ? 'Deselect all' : 'Select all'}
                    />
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-ink-muted">Account</th>
                  {sheetMeta && (
                    <th className="px-3 py-2 text-left font-medium text-ink-muted">Sheet</th>
                  )}
                  <th className="px-3 py-2 text-left font-medium text-ink-muted">Rate</th>
                  <th className="px-3 py-2 text-center font-medium text-ink-muted">Same</th>
                  <th className="px-3 py-2 text-right font-medium text-ink-muted">Points P&amp;L</th>
                  <th className="px-3 py-2 text-right font-medium text-ink-muted">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {displayRows.map((row, idx) => {
                  const isSelected = selected[row.accountId] === true;
                  const focusPos = focusableIdxs.indexOf(idx);
                  const uPts = row.uplinePoints.trim();
                  const cPts = row.equalSpread ? uPts : row.clientPoints.trim();
                  const uRate = row.assignment?.uplineRate ?? '';
                  const cRate = row.assignment?.clientRate ?? '';
                  const uAmt = uPts && uRate ? calcAmt(uPts, uRate) : '';
                  const cAmt = cPts && cRate ? calcAmt(cPts, cRate) : '';
                  const vr = verifyByAccount[row.accountId];

                  return (
                    <tr
                      key={row.accountId}
                      className={`transition-colors hover:bg-surface-raised/40 ${
                        isSelected ? '' : 'opacity-40'
                      }`}
                    >
                      <td className="px-2 py-2 text-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-border bg-surface accent-brand"
                          checked={isSelected}
                          onChange={(e) =>
                            setSelected((p) => ({ ...p, [row.accountId]: e.target.checked }))
                          }
                        />
                      </td>

                      <td className="px-3 py-2 font-medium text-ink">
                        {row.canonicalName}
                      </td>

                      {sheetMeta && (
                        <td className="px-3 py-2 align-top">
                          <SheetCell vr={vr} isSelected={isSelected} uPts={uPts} uRate={uRate} />
                        </td>
                      )}

                      <td className="px-3 py-2">
                        {ratesQ.isLoading ? (
                          <span className="text-ink-muted">…</span>
                        ) : row.assignment === null ? (
                          <span className="text-caption text-warn">no assignment</span>
                        ) : row.equalSpread ? (
                          <span className="font-mono text-ink-muted">{fmtPct(row.assignment.uplineRate)}</span>
                        ) : (
                          <div className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-1">
                              <span className="w-3 text-[10px] font-medium text-brand">U</span>
                              <span className="font-mono text-ink-muted">{fmtPct(row.assignment.uplineRate)}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="w-3 text-[10px] font-medium text-pos">C</span>
                              <span className="font-mono text-ink-muted">{fmtPct(row.assignment.clientRate)}</span>
                            </div>
                          </div>
                        )}
                      </td>

                      <td className="px-3 py-2 text-center">
                        <label className="inline-flex cursor-pointer flex-col items-center gap-0.5">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-border bg-surface accent-brand"
                            checked={row.equalSpread}
                            onChange={(e) =>
                              setSpreads((p) => ({ ...p, [row.accountId]: e.target.checked }))
                            }
                          />
                        </label>
                      </td>

                      <td className="px-3 py-2">
                        {row.assignment === null ? (
                          <span className="text-caption text-ink-muted">—</span>
                        ) : row.equalSpread ? (
                          <input
                            ref={(el) => { uplineRefs.current[idx] = el; }}
                            type="text"
                            inputMode="decimal"
                            value={row.uplinePoints}
                            placeholder="0.00"
                            disabled={!isSelected}
                            className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-right font-mono text-small text-ink placeholder:text-ink-muted focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/30 disabled:cursor-not-allowed disabled:opacity-50"
                            onChange={(e) =>
                              setUplinePoints((p) => ({ ...p, [row.accountId]: e.target.value }))
                            }
                            onKeyDown={(e) => {
                              if (e.key === 'Tab' && !e.shiftKey && focusPos !== -1) {
                                e.preventDefault();
                                const next = focusableIdxs[focusPos + 1];
                                if (next !== undefined) uplineRefs.current[next]?.focus();
                              }
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const next = focusableIdxs[focusPos + 1];
                                if (next !== undefined) {
                                  uplineRefs.current[next]?.focus();
                                } else if (canSave) {
                                  void withPin(() => saveMut.mutateAsync());
                                }
                              }
                            }}
                          />
                        ) : (
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-1.5">
                              <span className="w-4 shrink-0 text-[10px] font-medium text-brand">U</span>
                              <input
                                ref={(el) => { uplineRefs.current[idx] = el; }}
                                type="text"
                                inputMode="decimal"
                                value={row.uplinePoints}
                                placeholder="0.00"
                                disabled={!isSelected}
                                className="w-full rounded-md border border-border bg-surface px-2 py-1 text-right font-mono text-small text-ink placeholder:text-ink-muted focus:border-brand focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                                onChange={(e) =>
                                  setUplinePoints((p) => ({ ...p, [row.accountId]: e.target.value }))
                                }
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) {
                                    e.preventDefault();
                                    clientRefs.current[idx]?.focus();
                                  }
                                }}
                              />
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className="w-4 shrink-0 text-[10px] font-medium text-pos">C</span>
                              <input
                                ref={(el) => { clientRefs.current[idx] = el; }}
                                type="text"
                                inputMode="decimal"
                                value={row.clientPoints}
                                placeholder="0.00"
                                disabled={!isSelected}
                                className="w-full rounded-md border border-border bg-surface px-2 py-1 text-right font-mono text-small text-ink placeholder:text-ink-muted focus:border-pos focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                                onChange={(e) =>
                                  setClientPoints((p) => ({ ...p, [row.accountId]: e.target.value }))
                                }
                                onKeyDown={(e) => {
                                  if (e.key === 'Tab' && !e.shiftKey && focusPos !== -1) {
                                    e.preventDefault();
                                    const next = focusableIdxs[focusPos + 1];
                                    if (next !== undefined) uplineRefs.current[next]?.focus();
                                  }
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    const next = focusableIdxs[focusPos + 1];
                                    if (next !== undefined) {
                                      uplineRefs.current[next]?.focus();
                                    } else if (canSave) {
                                      void withPin(() => saveMut.mutateAsync());
                                    }
                                  }
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </td>

                      <td className="px-3 py-2">
                        {row.equalSpread ? (
                          <p className="text-right font-mono text-small text-ink">
                            {uAmt !== '' ? uAmt : <span className="text-ink-muted">—</span>}
                          </p>
                        ) : (
                          <div className="flex flex-col items-end gap-1">
                            <div className="flex items-center gap-1.5">
                              <span className="text-[10px] font-medium text-brand">U</span>
                              <span className="font-mono text-caption text-ink-muted">
                                {uAmt !== '' ? uAmt : '—'}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className="text-[10px] font-medium text-pos">C</span>
                              <span className="font-mono text-small text-ink">
                                {cAmt !== '' ? cAmt : '—'}
                              </span>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {error && <ErrorBanner>{error}</ErrorBanner>}

          <div className="flex items-center justify-between gap-4">
            <span className="text-caption text-ink-muted">
              {rows.length > 0
                ? `${selectedCount} of ${rows.length} row(s) selected — blank entries default to 0`
                : ''}
            </span>
            <div className="flex shrink-0 gap-2">
              <Button variant="ghost" onClick={onClose} disabled={saveMut.isPending}>
                Cancel
              </Button>
              <Button
                onClick={() => void withPin(() => saveMut.mutateAsync())}
                disabled={saveMut.isPending || !canSave || isLoading}
              >
                {saveMut.isPending ? <Spinner /> : `Save ${rowsToSave.length} row(s)`}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

interface ListMobileProps {
  rows: GridRow[];
  verifyByAccount: Record<number, SheetVerifiedRow>;
  hasSheet: boolean;
  ratesLoading: boolean;
  uplinePoints: Record<number, string>;
  setUplinePoints: React.Dispatch<React.SetStateAction<Record<number, string>>>;
  clientPoints: Record<number, string>;
  setClientPoints: React.Dispatch<React.SetStateAction<Record<number, string>>>;
  spreads: Record<number, boolean>;
  setSpreads: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
  selected: Record<number, boolean>;
  setSelected: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
  saveCount: number;
  selectedCount: number;
  totalRows: number;
  canSave: boolean;
  onSave: () => void;
  isSaving: boolean;
  onCancel: () => void;
  error: string | null;
}

/**
 * Mobile manual-entry: a scrolling LIST of stacked cards — one per account —
 * with no horizontal scroll (every input is full width). Replaces the old
 * one-at-a-time stepper so the user can scan all accounts, see which still
 * need points, and fill them top-to-bottom. A sticky bottom bar keeps Save
 * reachable without scrolling to the end. Save semantics are identical to the
 * desktop table (same shared state → same `saveMut`/`rowsToSave` builder).
 */
function ManualEntryListMobile({
  rows,
  verifyByAccount,
  hasSheet,
  ratesLoading,
  uplinePoints,
  setUplinePoints,
  clientPoints,
  setClientPoints,
  spreads,
  setSpreads,
  selected,
  setSelected,
  saveCount,
  selectedCount,
  totalRows,
  canSave,
  onSave,
  isSaving,
  onCancel,
  error,
}: ListMobileProps) {
  const uplineRefs = useRef<(HTMLInputElement | null)[]>([]);
  const clientRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Enter/Tab traversal chain over selectable rows only (has a rate AND is
  // selected); last input → Save (PIN-wrapped by the caller).
  const focusableIdxs = rows.reduce<number[]>((acc, r, i) => {
    if (r.assignment !== null && selected[r.accountId] === true) acc.push(i);
    return acc;
  }, []);
  const focusNextUpline = (idx: number) => {
    const pos = focusableIdxs.indexOf(idx);
    const next = focusableIdxs[pos + 1];
    if (next !== undefined) uplineRefs.current[next]?.focus();
    else if (canSave) onSave();
  };

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-3">
        {rows.map((row, idx) => {
          const { accountId } = row;
          const isSelected = selected[accountId] === true;
          const noAssign = row.assignment === null;
          const uPts = (uplinePoints[accountId] ?? '').trim();
          const cPts = row.equalSpread ? uPts : (clientPoints[accountId] ?? '').trim();
          const uRate = row.assignment?.uplineRate ?? '';
          const cRate = row.assignment?.clientRate ?? '';
          const uAmt = uPts && uRate ? calcAmt(uPts, uRate) : '';
          const cAmt = cPts && cRate ? calcAmt(cPts, cRate) : '';

          return (
            <div
              key={accountId}
              className={`overflow-hidden rounded-[22px] border app-shell-surface p-4 shadow-sm backdrop-blur transition-opacity ${
                isSelected ? 'border-brand/30' : 'border-border/70 opacity-60'
              }`}
            >
              {/* Header: include checkbox + name + rate */}
              <label className="flex min-h-[44px] cursor-pointer items-center gap-3">
                <input
                  type="checkbox"
                  checked={isSelected}
                  disabled={noAssign}
                  onChange={(e) => setSelected((p) => ({ ...p, [accountId]: e.target.checked }))}
                  className="h-5 w-5 shrink-0 rounded border-border bg-surface accent-brand disabled:opacity-50"
                />
                <span className="min-w-0 flex-1 truncate text-body font-semibold text-ink">
                  {row.canonicalName}
                </span>
                {ratesLoading ? (
                  <span className="shrink-0 text-caption text-ink-muted">…</span>
                ) : row.assignment === null ? (
                  <span className="shrink-0 text-caption text-warn">no assignment</span>
                ) : row.equalSpread ? (
                  <span className="shrink-0 font-mono text-caption text-ink-muted">
                    {fmtPct(row.assignment.uplineRate)}
                  </span>
                ) : (
                  <span className="shrink-0 font-mono text-caption text-ink-muted">
                    <span className="text-brand">U</span> {fmtPct(row.assignment.uplineRate)}{'  '}
                    <span className="text-pos">C</span> {fmtPct(row.assignment.clientRate)}
                  </span>
                )}
              </label>

              {hasSheet && (
                <div className="mt-2">
                  <SheetCell
                    vr={verifyByAccount[accountId]}
                    isSelected={isSelected}
                    uPts={uPts}
                    uRate={uRate}
                  />
                </div>
              )}

              {!noAssign && (
                <>
                  {/* Same toggle (per-entry only) */}
                  <label className="mt-2 flex min-h-[44px] cursor-pointer items-center justify-between gap-3">
                    <span className="text-small text-ink-muted">Same points for U &amp; C</span>
                    <input
                      type="checkbox"
                      checked={row.equalSpread}
                      onChange={(e) => setSpreads((p) => ({ ...p, [accountId]: e.target.checked }))}
                      className="h-5 w-5 rounded border-border bg-surface accent-brand"
                    />
                  </label>

                  {row.equalSpread ? (
                    <div className="mt-2 space-y-1.5">
                      <label className="text-caption font-medium text-ink-muted">Points P&amp;L</label>
                      <input
                        ref={(el) => { uplineRefs.current[idx] = el; }}
                        type="text"
                        inputMode="decimal"
                        value={uplinePoints[accountId] ?? ''}
                        placeholder="0.00"
                        disabled={!isSelected}
                        onFocus={(e) => e.target.select()}
                        className="min-h-[48px] w-full rounded-xl border border-border bg-surface px-4 py-3 text-right font-mono text-base text-ink placeholder:text-ink-muted focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30 disabled:opacity-50"
                        onChange={(e) => setUplinePoints((p) => ({ ...p, [accountId]: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) {
                            e.preventDefault();
                            focusNextUpline(idx);
                          }
                        }}
                      />
                      <p className="text-right font-mono text-small text-ink-muted">{uAmt !== '' ? uAmt : '—'}</p>
                    </div>
                  ) : (
                    <div className="mt-2 space-y-3">
                      <div className="space-y-1.5">
                        <label className="text-caption font-medium text-brand">U — Upline points</label>
                        <input
                          ref={(el) => { uplineRefs.current[idx] = el; }}
                          type="text"
                          inputMode="decimal"
                          value={uplinePoints[accountId] ?? ''}
                          placeholder="0.00"
                          disabled={!isSelected}
                          onFocus={(e) => e.target.select()}
                          className="min-h-[48px] w-full rounded-xl border border-border bg-surface px-4 py-3 text-right font-mono text-base text-ink placeholder:text-ink-muted focus:border-brand focus:outline-none disabled:opacity-50"
                          onChange={(e) => setUplinePoints((p) => ({ ...p, [accountId]: e.target.value }))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) {
                              e.preventDefault();
                              clientRefs.current[idx]?.focus();
                            }
                          }}
                        />
                        <p className="text-right font-mono text-caption text-brand">{uAmt !== '' ? uAmt : '—'}</p>
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-caption font-medium text-pos">C — Client points</label>
                        <input
                          ref={(el) => { clientRefs.current[idx] = el; }}
                          type="text"
                          inputMode="decimal"
                          value={clientPoints[accountId] ?? ''}
                          placeholder="0.00"
                          disabled={!isSelected}
                          onFocus={(e) => e.target.select()}
                          className="min-h-[48px] w-full rounded-xl border border-border bg-surface px-4 py-3 text-right font-mono text-base text-ink placeholder:text-ink-muted focus:border-pos focus:outline-none disabled:opacity-50"
                          onChange={(e) => setClientPoints((p) => ({ ...p, [accountId]: e.target.value }))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) {
                              e.preventDefault();
                              focusNextUpline(idx);
                            }
                          }}
                        />
                        <p className="text-right font-mono text-caption text-pos">{cAmt !== '' ? cAmt : '—'}</p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {error && (
        <div className="mt-3">
          <ErrorBanner>{error}</ErrorBanner>
        </div>
      )}

      {/* Sticky action bar — pinned to the modal's scroll viewport so Save is
          always reachable. -mx-4/px-4 cancels the modal body's p-4 padding. */}
      <div className="sticky bottom-0 z-10 -mx-4 mt-3 border-t border-border bg-surface/95 px-4 py-3 backdrop-blur">
        <p className="mb-2 text-center text-caption text-ink-muted">
          {selectedCount} of {totalRows} selected
          {!canSave && ' — select at least one account'}
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Button variant="ghost" onClick={onCancel} disabled={isSaving} className="w-full">
            Cancel
          </Button>
          <Button onClick={onSave} disabled={isSaving || !canSave} className="w-full">
            {isSaving ? <Spinner /> : `Save ${saveCount} row(s)`}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Reconciliation components ────────────────────────────────────────────────

/**
 * Per-row reconciliation cell: how the entered points reconcile against the
 * pasted sheet — the at-a-glance signal that catches the six Batch-#6 mistakes.
 */
function SheetCell({
  vr,
  isSelected,
  uPts,
  uRate,
}: {
  vr: SheetVerifiedRow | undefined;
  isSelected: boolean;
  uPts: string;
  uRate: string;
}) {
  if (!vr) {
    // Selected but the sheet never mentioned this account → an EXTRA row.
    return isSelected ? (
      <Tag tone="neg">extra — not on sheet</Tag>
    ) : (
      <span className="text-ink-muted">—</span>
    );
  }
  const divergent =
    vr.clientPoints !== undefined &&
    vr.uplinePoints !== undefined &&
    vr.clientPoints !== vr.uplinePoints;
  const computed = uPts && uRate ? amtNum(uPts, uRate) : null;
  const sheetCost = vr.uplineCost !== undefined ? parseFloat(vr.uplineCost) : null;
  const variance =
    computed !== null && sheetCost !== null && Math.abs(computed - sheetCost) > 0.01;

  return (
    <div className="flex flex-col items-start gap-1">
      <Tag tone={statusTone(vr.status)}>{statusLabel(vr.status)}</Tag>
      {divergent && (
        <span className="font-mono text-[10px] text-ink">
          C {vr.clientPoints} ≠ U {vr.uplinePoints}
        </span>
      )}
      {variance && (
        <span className="font-mono text-[10px] text-warn">
          cost {fmtMoney(computed!)} vs sheet {fmtMoney(sheetCost!)}
        </span>
      )}
      {(vr.diffs ?? []).slice(0, 2).map((d, i) => (
        <span key={i} className="font-mono text-[10px] text-ink-muted">
          {d.field}: {d.sheet} → {d.system}
        </span>
      ))}
      {vr.status === 'CLIENT_MISMATCH' && vr.expectedClientName && (
        <span className="text-[10px] text-ink-muted">expected {vr.expectedClientName}</span>
      )}
    </div>
  );
}

/** Running totals + sheet-order toggle, shown once after a paste. */
function ReconciliationBar({
  totals,
  totalMargin,
  rowCount,
  sheetOrderMode,
  setSheetOrderMode,
}: {
  totals: { uplineCost: number; clientAmount: number };
  totalMargin: number;
  rowCount: number;
  sheetOrderMode: boolean;
  setSheetOrderMode: (v: boolean) => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-surface-raised/40 px-3 py-2">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-caption">
        <span className="text-ink-muted">{rowCount} selected · totals:</span>
        <span className="font-mono text-brand">upline {fmtMoney(totals.uplineCost)}</span>
        <span className="font-mono text-pos">client {fmtMoney(totals.clientAmount)}</span>
        <span className={`font-mono font-semibold ${totalMargin >= 0 ? 'text-pos' : 'text-neg'}`}>
          margin {fmtMoney(totalMargin)}
        </span>
      </div>
      <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-caption text-ink-muted">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-border bg-surface accent-brand"
          checked={sheetOrderMode}
          onChange={(e) => setSheetOrderMode(e.target.checked)}
        />
        Sheet order
      </label>
    </div>
  );
}

/** Unmatched sheet names (missed/new accounts) + extra entered rows. */
function ExceptionsPanel({
  unresolved,
  extraNames,
}: {
  unresolved: SheetVerifiedRow[];
  extraNames: string[];
}) {
  const counts = [
    unresolved.length ? `${unresolved.length} unmatched` : '',
    extraNames.length ? `${extraNames.length} extra` : '',
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-caption text-amber-800">
      <div className="font-semibold">Needs attention ({counts})</div>
      <ul className="mt-1.5 space-y-1">
        {unresolved.map((e, i) => (
          <li key={`u${i}`}>
            <span className="font-medium">{e.rawAccountName ?? '(blank)'}</span> — on the sheet but
            matches no account. {e.reason ?? ''}
          </li>
        ))}
        {extraNames.map((nm, i) => (
          <li key={`x${i}`}>
            <span className="font-medium">{nm}</span> — entered but not on the sheet.
          </li>
        ))}
      </ul>
      <p className="mt-1.5 text-[11px] text-amber-700">
        Unmatched names are likely a missed or new account — create/attach it from the Accounts page,
        then re-paste. Extra rows are ones the upline didn’t list.
      </p>
    </div>
  );
}
